/**
 * Page /grc — Dashboard GRC & Communication (Phase 2A étendue L15a).
 *
 *   - 4 KPIs : Demandes en cours / Délai réponse SLA / Abonnés newsletter / Push PWA opt-in
 *   - Boîte citoyenne unifiée (sig + grc_message) avec filtres canal/service/statut
 *   - Top sujets du mois (badges colorés par catégorie)
 *   - Sidebar droite : Système d'alerte (placeholder L15b) + Newsletter (placeholder L15c)
 *
 * Refonte R7.1 (mai 2026) :
 *   - Topbar glass cohérent avec halo grc (au lieu de bg-grc plein)
 *   - Sub-nav avec tabs en pills glass (Dashboard actif / Inbox / Alertes / Newsletter)
 *   - LogoMark MessageCircle accent grc
 *   - KpiGrid en cards glass avec halo radial accent
 *   - InboxTable en table glass avec rows hover (rangées denses pour avoir un max de visibilité)
 *   - CreateMessageModal en bg-ink/60 + backdrop-blur-md
 *   - AlertesPanel + NewsletterPanel en Card glass
 *
 * Réservé à super_admin / admin / manager / agent (agent lecture seule).
 *
 * Logique métier 100% préservée :
 *   - api.grc.dashboard() + api.grc.inbox(filters) + api.grc.messages.create()
 *   - api.grc.alertes.list({statut: 'active', limit: 3}) pour AlertesPanel
 *   - api.grc.newsletters.list({limit: 3}) pour NewsletterPanel
 *   - Filtres : tab (tous/a_traiter/mes) + canal + service + search avec Enter→reload
 *   - Maps : CANAUX_ICON, CANAUX_LABEL, SERVICES_BADGE, CATEGORIE_BADGE
 *   - navigateToItem : signalement → /service-technique, autre → /grc/messages/:id
 *   - isReadOnly = agent (pas de bouton Saisir un message)
 *   - Niveau alerte color (info=success, avis=warning, alerte=urgent, crise=ink)
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiException } from '@/lib/api';
import { useAuth } from '@/lib/auth-context';
import { Button, Badge, Card, Alert, LogoMark, SectionEyebrow } from '@/components/ui';
import { ThemeToggle } from '@/components/ThemeToggle';
import { GrcLayout } from '@/components/grc/GrcLayout';
import { SelecteurAlerte } from '@/components/grc/SelecteurAlerte';
import {
  MessageCircle, Inbox, Clock, MailOpen, BellRing,
  Mail, FileText, Phone, FormInput, Building2, Plus,
  AlertCircle, Landmark, Search, X, ChevronRight, ArrowLeft,
} from 'lucide-react';

const CANAUX_ICON: Record<string, typeof Mail> = {
  email:        Mail,
  courrier:     FileText,
  signalement:  AlertCircle,
  formulaire:   FormInput,
  message_asso: MessageCircle,
  telephone:    Phone,
  guichet:      Building2,
  autre:        FileText,
};

const CANAUX_LABEL: Record<string, string> = {
  email:        'Email',
  courrier:     'Courrier',
  signalement:  'Signalement',
  formulaire:   'Formulaire',
  message_asso: 'Message Asso',
  telephone:    'Téléphone',
  guichet:      'Guichet',
  autre:        'Autre',
};

// Mappings statiques Badge variants (Tailwind purge-safe)
const SERVICES_VARIANT: Record<string, 'tech' | 'asso' | 'ecole' | 'urba' | 'grc' | 'cantine' | 'neutral'> = {
  service_technique: 'tech',
  associations:      'asso',
  scolaire:          'ecole',
  urbanisme:         'urba',
  grc:               'grc',
  cantine:           'cantine',
  etat_civil:        'neutral',
  autre:             'neutral',
};

const SERVICES_SHORT: Record<string, string> = {
  service_technique: 'Tech',
  associations:      'Asso',
  scolaire:          'École',
  urbanisme:         'Urba',
  grc:               'GRC',
  cantine:           'Cantine',
  etat_civil:        'État civil',
  autre:             'Autre',
};

const CATEGORIE_VARIANT: Record<string, 'tech' | 'asso' | 'ecole' | 'urba' | 'grc' | 'cantine' | 'warning' | 'neutral'> = {
  voirie:           'tech',
  locations_salles: 'asso',
  scolarite:        'ecole',
  etat_civil:       'neutral',
  urbanisme:        'urba',
  cantine:          'cantine',
  environnement:    'urba',
  animaux:          'warning',
  eclairage:        'ecole',
  proprete:         'grc',
  evenement:        'asso',
  autre:            'neutral',
};

type Dashboard = Awaited<ReturnType<typeof api.grc.dashboard>>;
type InboxResult = Awaited<ReturnType<typeof api.grc.inbox>>;
type InboxItem = InboxResult['items'][number];

// Topbar style partagé (radial halo grc + linear bg-2 → bg)
const TOPBAR_BG = {
  backgroundImage:
    'radial-gradient(220px 80px at 10% 50%, oklch(var(--c-grc) / 0.22), transparent 70%), ' +
    'linear-gradient(90deg, oklch(var(--c-bg-2) / 0.65), oklch(var(--c-bg) / 0.45))',
};

// =============================================================================
// PAGE PRINCIPALE
// =============================================================================

export function GrcDashboardPage() {
  const navigate = useNavigate();
  const { tenant, user } = useAuth();
  const [dashboard, setDashboard] = useState<Dashboard | null>(null);
  const [inbox, setInbox] = useState<InboxResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filtres inbox
  const [filterTab, setFilterTab] = useState<'tous' | 'a_traiter' | 'mes'>('a_traiter');
  const [filterCanal, setFilterCanal] = useState<string>('');
  const [filterService, setFilterService] = useState<string>('');
  const [filterSearch, setFilterSearch] = useState('');

  // Modal nouveau message
  const [showCreate, setShowCreate] = useState(false);

  const isReadOnly = user?.role === 'agent';

  async function reload() {
    setLoading(true); setError(null);
    try {
      const filters: Parameters<typeof api.grc.inbox>[0] = {
        limit: 50,
      };
      if (filterTab === 'a_traiter') filters.statut = 'open';
      if (filterTab === 'mes') filters.assigneUserId = 'me';
      if (filterCanal) filters.canal = filterCanal;
      if (filterService) filters.service = filterService;
      if (filterSearch.trim()) filters.search = filterSearch.trim();

      const [dash, inboxRes] = await Promise.all([
        api.grc.dashboard(),
        api.grc.inbox(filters),
      ]);
      setDashboard(dash);
      setInbox(inboxRes);
    } catch (err) {
      setError(err instanceof ApiException ? err.payload.error : 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void reload(); /* eslint-disable-next-line */ }, [filterTab, filterCanal, filterService]);

  function navigateToItem(item: InboxItem) {
    if (item.source === 'signalement') {
      navigate(`/service-technique/signalements/${item.nativeId}`);
    } else {
      navigate(`/grc/messages/${item.nativeId}`);
    }
  }

  return (
    <GrcLayout active="dashboard">
      <main id="main-content" className="mx-auto max-w-[1400px] px-4 py-5 animate-fade-in">
        {/* En-tête de page : LogoMark + titre + meta */}
        <div className="mb-5 flex items-center gap-3">
          <LogoMark icon={MessageCircle} accent="grc" size="lg" />
          <div>
            <SectionEyebrow as="div">GRC & communication</SectionEyebrow>
            <h1 className="mt-0.5 font-display text-xl font-semibold tracking-r-snug text-ink sm:text-2xl">
              Dashboard
            </h1>
          </div>
          {!isReadOnly && (
            <Button type="button" variant="grc" size="sm" className="ml-auto" onClick={() => setShowCreate(true)}>
              <Plus className="h-4 w-4" aria-hidden="true" />
              <span className="hidden sm:inline">Saisir un message</span>
            </Button>
          )}
        </div>

        {error && (
          <Alert variant="error" icon={AlertCircle} className="mb-3">
            {error}
          </Alert>
        )}

        {loading ? (
          <Card variant="glass" padded>
            <div className="py-8 text-center">
              <div
                className="mx-auto h-10 w-10 animate-spin rounded-full border-2 border-grc border-t-transparent"
                role="status"
                aria-live="polite"
              />
              <p className="mt-4 text-sm text-ink-muted">Chargement du dashboard…</p>
            </div>
          </Card>
        ) : dashboard && inbox ? (
          <>
            {/* KPIs */}
            <KpiGrid kpis={dashboard.kpis} />

            <div className="mt-4 grid gap-4 lg:grid-cols-[1fr_300px]">
              {/* Inbox unifiée */}
              <Card variant="glass" padded>
                <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
                  <h2 className="font-display text-base font-semibold tracking-r-snug text-ink">
                    Boîte citoyenne unifiée
                  </h2>
                  <div className="flex flex-wrap gap-1.5">
                    <TabPill active={filterTab === 'tous'} onClick={() => setFilterTab('tous')}>Tous</TabPill>
                    <TabPill active={filterTab === 'a_traiter'} onClick={() => setFilterTab('a_traiter')} tone="grc">À traiter</TabPill>
                    <TabPill active={filterTab === 'mes'} onClick={() => setFilterTab('mes')}>Mes dossiers</TabPill>
                  </div>
                </div>

                {/* Filtres avancés */}
                <div className="mb-3 flex flex-wrap items-center gap-2 rounded-r-lg border border-border bg-surface/40 backdrop-blur-glass p-2">
                  <div className="relative flex-1 min-w-[200px]">
                    <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink-dim" aria-hidden="true" />
                    <input
                      type="search"
                      value={filterSearch}
                      onChange={(e) => setFilterSearch(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') reload(); }}
                      placeholder="Rechercher (sujet, contenu, citoyen)…"
                      className="h-9 w-full rounded-r-lg border border-border bg-surface/60 backdrop-blur-glass pl-8 pr-2 text-xs text-ink placeholder:text-ink-dim shadow-soft transition-all focus:border-grc focus:outline-none focus:ring-2 focus:ring-grc focus:ring-offset-2 focus:ring-offset-bg hover:border-border-strong"
                      aria-label="Rechercher dans l'inbox"
                    />
                  </div>
                  <select
                    aria-label="Filtrer par canal"
                    value={filterCanal}
                    onChange={(e) => setFilterCanal(e.target.value)}
                    className="h-9 rounded-r-lg border border-border bg-surface/60 backdrop-blur-glass px-2.5 text-xs text-ink shadow-soft transition-all focus:border-grc focus:outline-none focus:ring-2 focus:ring-grc focus:ring-offset-2 focus:ring-offset-bg hover:border-border-strong"
                  >
                    <option value="">Tous canaux</option>
                    <option value="signalement">Signalement</option>
                    <option value="email">Email</option>
                    <option value="courrier">Courrier</option>
                    <option value="formulaire">Formulaire</option>
                    <option value="telephone">Téléphone</option>
                    <option value="guichet">Guichet</option>
                  </select>
                  <select
                    aria-label="Filtrer par service"
                    value={filterService}
                    onChange={(e) => setFilterService(e.target.value)}
                    className="h-9 rounded-r-lg border border-border bg-surface/60 backdrop-blur-glass px-2.5 text-xs text-ink shadow-soft transition-all focus:border-grc focus:outline-none focus:ring-2 focus:ring-grc focus:ring-offset-2 focus:ring-offset-bg hover:border-border-strong"
                  >
                    <option value="">Tous services</option>
                    <option value="service_technique">Service technique</option>
                    <option value="associations">Associations</option>
                    <option value="scolaire">Scolaire</option>
                    <option value="urbanisme">Urbanisme</option>
                    <option value="grc">GRC</option>
                    <option value="cantine">Cantine</option>
                    <option value="etat_civil">État civil</option>
                  </select>
                </div>

                {/* Tableau inbox */}
                <InboxTable items={inbox.items} onClickItem={navigateToItem} />

                {inbox.total > inbox.items.length && (
                  <div className="mt-3 text-center">
                    <Button type="button" variant="ghost" size="sm" onClick={() => navigate('/grc/messages')}>
                      Voir tout l'inbox ({inbox.total})
                      <ChevronRight className="h-4 w-4" aria-hidden="true" />
                    </Button>
                  </div>
                )}

                {/* Top sujets du mois */}
                <div className="mt-5 border-t border-border pt-4">
                  <SectionEyebrow as="div" className="mb-2">Top sujets du mois</SectionEyebrow>
                  {dashboard.topSujets.length === 0 ? (
                    <p className="text-xs text-ink-dim">Aucune donnée ce mois-ci</p>
                  ) : (
                    <div className="flex flex-wrap gap-1.5">
                      {dashboard.topSujets.map((sujet) => (
                        <Badge
                          key={sujet.categorie}
                          variant={CATEGORIE_VARIANT[sujet.categorie] ?? 'neutral'}
                          size="sm"
                        >
                          {sujet.label} · {sujet.count}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              </Card>

              {/* Sidebar droite : alertes (L15b) + newsletter (L15c) */}
              <aside aria-label="Communication" className="space-y-3">
                <AlertesPanel onNavigate={navigate} />
                <NewsletterPanel onNavigate={navigate} abonnes={dashboard.kpis.abonnesNewsletter} />
              </aside>
            </div>
          </>
        ) : null}
      </main>

      {showCreate && (
        <CreateMessageModal
          onClose={() => setShowCreate(false)}
          onCreated={async () => { setShowCreate(false); await reload(); }}
        />
      )}
    </GrcLayout>
  );
}

// =============================================================================
// KPI GRID (4 cartes)
// =============================================================================

function KpiGrid({ kpis }: { kpis: Dashboard['kpis'] }) {
  const slaOk = kpis.delaiMoyenReponseJours <= kpis.slaReponseJours;
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      <GrcKpi
        icon={Inbox}
        label="Demandes en cours"
        value={String(kpis.demandesEnCours)}
        sub="tous canaux confondus"
        accent="grc"
      />
      <GrcKpi
        icon={Clock}
        label="Délai moyen réponse"
        value={`${kpis.delaiMoyenReponseJours.toFixed(1).replace('.', ',')} j`}
        sub={`SLA interne : ${kpis.slaReponseJours}j`}
        accent={slaOk ? 'neutral' : 'warning'}
      />
      <GrcKpi
        icon={MailOpen}
        label="Abonnés newsletter"
        value={String(kpis.abonnesNewsletter)}
        sub={
          kpis.abonnesNewsletter7jVariation > 0
            ? `+${kpis.abonnesNewsletter7jVariation} sur 7j`
            : kpis.abonnesNewsletter7jVariation < 0
              ? `${kpis.abonnesNewsletter7jVariation} sur 7j`
              : 'stable sur 7j'
        }
        accent="neutral"
      />
      <GrcKpi
        icon={BellRing}
        label="Push PWA opt-in"
        value={String(kpis.pushPwaOptIn)}
        sub={`${kpis.pushPwaOptInPct} % des comptes`}
        accent="neutral"
      />
    </div>
  );
}

function GrcKpi({
  icon: Icon, label, value, sub, accent,
}: {
  icon: typeof Inbox;
  label: string;
  value: string;
  sub: string;
  accent: 'grc' | 'neutral' | 'warning';
}) {
  // Mappings statiques pour Tailwind purge-safe
  const styles = {
    grc: {
      border: 'border-grc/30',
      halo: 'radial-gradient(160px 80px at 0% 0%, oklch(var(--c-grc) / 0.22), transparent 70%)',
      iconColor: 'text-grc-fg',
    },
    warning: {
      border: 'border-warning/30',
      halo: 'radial-gradient(160px 80px at 0% 0%, oklch(var(--c-warning) / 0.22), transparent 70%)',
      iconColor: 'text-warning',
    },
    neutral: {
      border: 'border-border',
      halo: 'none',
      iconColor: 'text-ink-muted',
    },
  }[accent];

  return (
    <div
      className={`rounded-r-xl border ${styles.border} bg-surface/55 backdrop-blur-glass p-3.5 shadow-soft transition-all hover:shadow-glass-hi`}
      style={{
        backgroundImage:
          styles.halo === 'none'
            ? undefined
            : `${styles.halo}, linear-gradient(180deg, oklch(var(--c-surface) / 0.55), oklch(var(--c-bg-2) / 0.45))`,
      }}
    >
      <div className={`flex items-center gap-1.5 text-xs ${styles.iconColor}`}>
        <Icon className="h-3.5 w-3.5" aria-hidden="true" />
        <span className="text-ink-muted">{label}</span>
      </div>
      <div className="mt-1.5 font-display text-2xl font-semibold leading-tight tracking-r-tight tabular-nums text-ink">
        {value}
      </div>
      <div className="mt-0.5 text-2xs text-ink-subtle">{sub}</div>
    </div>
  );
}

// =============================================================================
// INBOX TABLE
// =============================================================================

function InboxTable({
  items, onClickItem,
}: {
  items: InboxItem[];
  onClickItem: (item: InboxItem) => void;
}) {
  if (items.length === 0) {
    return (
      <div className="rounded-r-lg border border-dashed border-border bg-surface/30 backdrop-blur-glass px-4 py-8 text-center">
        <Inbox className="mx-auto mb-2 h-8 w-8 text-ink-dim" aria-hidden="true" />
        <p className="text-sm text-ink-muted">Aucun message ne correspond à ces filtres</p>
      </div>
    );
  }

  return (
    <>
      {/* ===== Cards stack mobile (< md) — pattern responsive table ===== */}
      <ul className="md:hidden space-y-2" role="list" aria-label="Liste des messages inbox">
        {items.map((item) => {
          const CanalIcon = CANAUX_ICON[item.canal] ?? FileText;
          const isOpen = item.statut === 'a_traiter' || item.statut === 'en_cours';
          const isUrgent = item.urgence === 'urgent';
          const date = new Date(item.receivedAt);
          const ageDays = Math.floor((Date.now() - date.getTime()) / 86400000);
          const ageLabel = ageDays === 0 ? 'auj.' : `${ageDays}j`;

          const serviceVariant = item.service ? SERVICES_VARIANT[item.service] : undefined;
          const serviceShort = item.service ? SERVICES_SHORT[item.service] : undefined;

          return (
            <li key={item.id}>
              <button
                type="button"
                onClick={() => onClickItem(item)}
                className={[
                  'block w-full text-left rounded-r-lg border backdrop-blur-glass p-3 transition-all',
                  'hover:bg-surface/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-grc focus-visible:ring-offset-2 focus-visible:ring-offset-bg',
                  isUrgent && isOpen
                    ? 'border-urgent/30 bg-urgent/5'
                    : 'border-border bg-surface/40',
                ].join(' ')}
              >
                {/* Ligne 1 : badge service + canal + age */}
                <div className="flex items-center justify-between gap-2 mb-1.5">
                  <div className="flex items-center gap-1.5 min-w-0 flex-wrap">
                    {serviceVariant && serviceShort && (
                      <Badge variant={serviceVariant} size="sm">{serviceShort}</Badge>
                    )}
                    <span className="inline-flex items-center gap-1 text-2xs text-ink-muted">
                      <CanalIcon
                        className={`h-3 w-3 ${isUrgent && isOpen ? 'text-urgent' : ''}`}
                        aria-hidden="true"
                      />
                      <span>{CANAUX_LABEL[item.canal]}</span>
                    </span>
                  </div>
                  <span className="text-2xs text-ink-subtle tabular-nums flex-shrink-0">{ageLabel}</span>
                </div>

                {/* Ligne 2 : sujet + chevron */}
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-ink text-sm line-clamp-1">{item.sujet}</div>
                    {item.apercu && (
                      <div className="mt-0.5 text-2xs text-ink-muted line-clamp-1">{item.apercu}</div>
                    )}
                    <div className="mt-1 flex items-center gap-2 text-2xs text-ink-subtle">
                      <span className="truncate">
                        {item.isAnonymous ? (
                          <span className="italic">Anonyme</span>
                        ) : (
                          item.citoyenNom ?? item.citoyenEmail ?? '—'
                        )}
                      </span>
                      {item.numero && (
                        <>
                          <span aria-hidden="true">·</span>
                          <span className="font-mono text-[10px]">{item.numero}</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </button>
            </li>
          );
        })}
      </ul>

      {/* ===== Table desktop (md+) ===== */}
      <div className="hidden md:block rounded-r-lg border border-border bg-surface/30 backdrop-blur-glass">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-border bg-surface/40 text-left">
              <th className="px-2 py-2 w-8"><span className="sr-only">Canal</span></th>
              <th className="px-2 py-2 w-24 text-2xs uppercase tracking-r-eyebrow text-ink-subtle font-medium">Canal</th>
              <th className="px-2 py-2 text-2xs uppercase tracking-r-eyebrow text-ink-subtle font-medium">Sujet</th>
              <th className="px-2 py-2 w-32 text-2xs uppercase tracking-r-eyebrow text-ink-subtle font-medium">Citoyen</th>
              <th className="px-2 py-2 w-24 text-2xs uppercase tracking-r-eyebrow text-ink-subtle font-medium">Service</th>
              <th className="px-2 py-2 w-16 text-2xs uppercase tracking-r-eyebrow text-ink-subtle font-medium">Reçu</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => {
              const CanalIcon = CANAUX_ICON[item.canal] ?? FileText;
              const isOpen = item.statut === 'a_traiter' || item.statut === 'en_cours';
              const isUrgent = item.urgence === 'urgent';
              const date = new Date(item.receivedAt);
              const ageDays = Math.floor((Date.now() - date.getTime()) / 86400000);
              const ageLabel = ageDays === 0 ? 'auj.' : `${ageDays}j`;

              const serviceVariant = item.service ? SERVICES_VARIANT[item.service] : undefined;
              const serviceShort = item.service ? SERVICES_SHORT[item.service] : undefined;

              return (
                <tr
                  key={item.id}
                  onClick={() => onClickItem(item)}
                  tabIndex={0}
                  role="link"
                  onKeyDown={(e) => { if (e.key === 'Enter') onClickItem(item); }}
                  className={[
                    'cursor-pointer border-b border-border last:border-b-0 transition-colors',
                    'hover:bg-surface/60 focus:bg-surface/60 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-grc/40',
                    isUrgent && isOpen ? 'bg-urgent/5 hover:bg-urgent/8' : '',
                  ].join(' ')}
                >
                  <td className="px-2 py-2 text-center">
                    <CanalIcon
                      className={`h-3.5 w-3.5 ${isUrgent && isOpen ? 'text-urgent' : 'text-ink-muted'}`}
                      aria-hidden="true"
                    />
                  </td>
                  <td className="px-2 py-2 text-ink-muted">{CANAUX_LABEL[item.canal]}</td>
                  <td className="px-2 py-2">
                    <div className="font-medium text-ink line-clamp-1">{item.sujet}</div>
                    {item.apercu && <div className="text-2xs text-ink-muted line-clamp-1">{item.apercu}</div>}
                    {item.numero && (
                      <span className="mt-0.5 inline-block font-mono text-[10px] text-ink-subtle">{item.numero}</span>
                    )}
                  </td>
                  <td className="px-2 py-2 text-ink-muted">
                    {item.isAnonymous ? (
                      <span className="italic">Anonyme</span>
                    ) : (
                      item.citoyenNom ?? item.citoyenEmail ?? '—'
                    )}
                  </td>
                  <td className="px-2 py-2">
                    {serviceVariant && serviceShort ? (
                      <Badge variant={serviceVariant} size="sm">{serviceShort}</Badge>
                    ) : (
                      <span className="text-2xs text-ink-dim">—</span>
                    )}
                  </td>
                  <td className="px-2 py-2 text-2xs text-ink-muted tabular-nums">{ageLabel}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

// =============================================================================
// TAB COMPOSANTS (sub-nav + tabs inbox)
// =============================================================================

function GrcTab({
  children, active, onClick,
}: {
  children: React.ReactNode;
  active?: boolean;
  onClick?: () => void;
}) {
  if (active) {
    return (
      <span className="inline-flex h-9 items-center gap-1 rounded-r-pill border border-grc/30 bg-grc-tint/80 px-3 text-xs font-medium text-grc-fg backdrop-blur-glass">
        {children}
      </span>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex h-9 items-center gap-1 rounded-r-pill border border-transparent bg-transparent px-3 text-xs font-medium text-ink-muted backdrop-blur-glass transition-all hover:border-border hover:bg-surface/40 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-grc focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
    >
      {children}
    </button>
  );
}

function TabPill({
  active, onClick, children, tone = 'neutral',
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
  tone?: 'neutral' | 'grc';
}) {
  // Mappings statiques Tailwind purge-safe
  const activeStyles = tone === 'grc'
    ? 'bg-gradient-grc text-white shadow-glow-grc border-grc-300/40'
    : 'bg-ink text-bg border-ink shadow-soft';

  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'inline-flex h-7 items-center justify-center rounded-r-pill border px-3 text-2xs font-medium transition-all',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-grc focus-visible:ring-offset-2 focus-visible:ring-offset-bg',
        active
          ? activeStyles
          : 'border-border bg-surface/40 backdrop-blur-glass text-ink-muted hover:bg-surface/70 hover:text-ink',
      ].join(' ')}
    >
      {children}
    </button>
  );
}

// =============================================================================
// MODAL CRÉATION MESSAGE (courrier, téléphone, guichet…)
// =============================================================================

function CreateMessageModal({
  onClose, onCreated,
}: {
  onClose: () => void;
  onCreated: () => Promise<void>;
}) {
  const [canal, setCanal] = useState('courrier');
  const [sujet, setSujet] = useState('');
  const [contenu, setContenu] = useState('');
  const [citoyenNom, setCitoyenNom] = useState('');
  const [citoyenEmail, setCitoyenEmail] = useState('');
  const [citoyenTelephone, setCitoyenTelephone] = useState('');
  const [service, setService] = useState('');
  const [categorieSujet, setCategorieSujet] = useState('');
  // Mig 234 — rattacher la saisie à une alerte : c'est ce qui la fait entrer
  // dans le journal de l'incident. Un appel reçu pendant une coupure d'eau
  // fait partie de la preuve de l'action municipale.
  const [alerteId, setAlerteId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true); setErr(null);
    try {
      await api.grc.messages.create({
        canal,
        sujet: sujet.trim(),
        contenu: contenu.trim() || undefined,
        citoyenNom: citoyenNom.trim() || undefined,
        citoyenEmail: citoyenEmail.trim() || undefined,
        citoyenTelephone: citoyenTelephone.trim() || undefined,
        service: service || undefined,
        categorieSujet: categorieSujet || undefined,
        alerteId: alerteId || undefined,
      });
      await onCreated();
    } catch (e2) {
      setErr(e2 instanceof ApiException ? e2.payload.error : 'Erreur');
    } finally {
      setSubmitting(false);
    }
  }

  // Styles partagés selects / inputs glass
  const inputCls = 'h-10 w-full rounded-r-lg border border-border bg-surface/60 backdrop-blur-glass px-3 text-sm text-ink shadow-soft transition-all focus:border-grc focus:outline-none focus:ring-2 focus:ring-grc focus:ring-offset-2 focus:ring-offset-bg hover:border-border-strong';
  const labelCls = 'block text-xs font-medium uppercase tracking-r-eyebrow text-ink-subtle mb-1';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink/60 backdrop-blur-md p-4"
        >
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md max-h-[90vh] overflow-y-auto">
        <Card variant="glass" padded>
          <div className="mb-3 flex items-start justify-between gap-2">
            <h2 className="font-display text-base font-semibold tracking-r-snug text-ink">
              Saisir un message reçu
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="rounded-r-md p-1 text-ink-muted transition-colors hover:bg-surface/60 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-grc focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
              aria-label="Fermer"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>

          {err && <Alert variant="error" icon={AlertCircle} className="mb-3">{err}</Alert>}

          <form onSubmit={handleSubmit} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="cm-canal" className={labelCls}>Canal *</label>
                <select id="cm-canal" value={canal} onChange={(e) => setCanal(e.target.value)} required className={inputCls}>
                  <option value="courrier">Courrier postal</option>
                  <option value="telephone">Appel téléphonique</option>
                  <option value="guichet">Guichet mairie</option>
                  <option value="email">Email</option>
                  <option value="formulaire">Formulaire web</option>
                  <option value="message_asso">Message Asso</option>
                  <option value="autre">Autre</option>
                </select>
              </div>
              <div>
                <label htmlFor="cm-service" className={labelCls}>Service destinataire</label>
                <select id="cm-service" value={service} onChange={(e) => setService(e.target.value)} className={inputCls}>
                  <option value="">— À orienter —</option>
                  <option value="service_technique">Service technique</option>
                  <option value="associations">Associations</option>
                  <option value="scolaire">Scolaire</option>
                  <option value="urbanisme">Urbanisme</option>
                  <option value="grc">GRC</option>
                  <option value="cantine">Cantine</option>
                  <option value="etat_civil">État civil</option>
                </select>
              </div>
            </div>

            <div>
              <label htmlFor="cm-sujet" className={labelCls}>Sujet *</label>
              <input
                id="cm-sujet"
                value={sujet}
                onChange={(e) => setSujet(e.target.value)}
                required minLength={2} maxLength={300}
                placeholder="Ex : Demande de salle pour AG"
                className={inputCls}
              />
            </div>

            <div>
              <label htmlFor="cm-contenu" className={labelCls}>Contenu / résumé</label>
              <textarea
                id="cm-contenu"
                value={contenu}
                onChange={(e) => setContenu(e.target.value)}
                rows={3} maxLength={20000}
                placeholder="Résumé de la demande ou retranscription de l'appel…"
                className="w-full rounded-r-lg border border-border bg-surface/60 backdrop-blur-glass p-2.5 text-sm text-ink shadow-soft transition-all focus:border-grc focus:outline-none focus:ring-2 focus:ring-grc focus:ring-offset-2 focus:ring-offset-bg hover:border-border-strong"
              />
            </div>

            <div className="rounded-r-lg border border-border bg-surface/30 backdrop-blur-glass p-3">
              <p className="mb-2 text-2xs font-medium uppercase tracking-r-eyebrow text-ink-subtle">
                Citoyen (au moins une info requise)
              </p>
              <div className="grid gap-2 sm:grid-cols-3">
                <input
                  value={citoyenNom}
                  onChange={(e) => setCitoyenNom(e.target.value)}
                  maxLength={150}
                  placeholder="Nom"
                  className="h-9 w-full rounded-r-lg border border-border bg-surface/60 backdrop-blur-glass px-2.5 text-xs text-ink shadow-soft transition-all focus:border-grc focus:outline-none focus:ring-2 focus:ring-grc focus:ring-offset-2 focus:ring-offset-bg hover:border-border-strong"
                />
                <input
                  value={citoyenEmail}
                  onChange={(e) => setCitoyenEmail(e.target.value)}
                  type="email"
                  maxLength={255}
                  placeholder="Email"
                  className="h-9 w-full rounded-r-lg border border-border bg-surface/60 backdrop-blur-glass px-2.5 text-xs text-ink shadow-soft transition-all focus:border-grc focus:outline-none focus:ring-2 focus:ring-grc focus:ring-offset-2 focus:ring-offset-bg hover:border-border-strong"
                />
                <input
                  value={citoyenTelephone}
                  onChange={(e) => setCitoyenTelephone(e.target.value)}
                  maxLength={30}
                  placeholder="Téléphone"
                  className="h-9 w-full rounded-r-lg border border-border bg-surface/60 backdrop-blur-glass px-2.5 text-xs text-ink shadow-soft transition-all focus:border-grc focus:outline-none focus:ring-2 focus:ring-grc focus:ring-offset-2 focus:ring-offset-bg hover:border-border-strong"
                />
              </div>
            </div>

            <div>
              <label htmlFor="cm-cat" className={labelCls}>Catégorie (top sujets)</label>
              <select id="cm-cat" value={categorieSujet} onChange={(e) => setCategorieSujet(e.target.value)} className={inputCls}>
                <option value="">— Aucune —</option>
                <option value="voirie">Voirie</option>
                <option value="locations_salles">Locations de salles</option>
                <option value="scolarite">Scolarité</option>
                <option value="etat_civil">État civil</option>
                <option value="urbanisme">Urbanisme</option>
                <option value="cantine">Cantine</option>
                <option value="environnement">Environnement</option>
                <option value="animaux">Animaux</option>
                <option value="eclairage">Éclairage</option>
                <option value="proprete">Propreté</option>
                <option value="evenement">Événement</option>
                <option value="autre">Autre</option>
              </select>
            </div>

            <SelecteurAlerte
              id="cm-alerte"
              value={alerteId}
              onChange={setAlerteId}
            />

            <div className="flex justify-end gap-2 pt-1">
              <Button type="button" variant="secondary" size="sm" onClick={onClose}>Annuler</Button>
              <Button type="submit" variant="grc" size="sm" disabled={submitting}>
                {submitting ? 'Enregistrement…' : 'Enregistrer'}
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
}

// =============================================================================
// ALERTES PANEL — encart sidebar dashboard
// =============================================================================

function AlertesPanel({ onNavigate }: { onNavigate: (path: string) => void }) {
  const [data, setData] = useState<{
    items: Array<{ id: string; niveau: string; titre: string; statut: string }>;
    activeCount: number;
  } | null>(null);

  useEffect(() => {
    let cancelled = false;
    void api.grc.alertes.list({ statut: 'active', limit: 3 }).then((d) => {
      if (!cancelled) setData(d);
    }).catch(() => { /* silent */ });
    return () => { cancelled = true; };
  }, []);

  const hasActive = (data?.activeCount ?? 0) > 0;

  // Mappings statiques niveau → couleur
  const niveauColor: Record<string, string> = {
    info:   'text-success',
    avis:   'text-warning',
    alerte: 'text-urgent',
    crise:  'text-ink',
  };

  return (
    <Card variant="glass" padded>
      <div className="mb-2 flex items-center justify-between">
        <SectionEyebrow as="h3">Système d'alerte</SectionEyebrow>
        <Badge variant={hasActive ? 'urgent' : 'success'} size="sm">
          {hasActive ? `${data?.activeCount} active${(data?.activeCount ?? 0) > 1 ? 's' : ''}` : 'Vigilance normale'}
        </Badge>
      </div>

      {hasActive && data?.items ? (
        <ul className="space-y-1.5">
          {data.items.map((a) => (
            <li key={a.id}>
              <button
                type="button"
                onClick={() => onNavigate(`/grc/alertes/${a.id}`)}
                className="block w-full rounded-r-md border border-border bg-surface/40 backdrop-blur-glass p-2 text-left transition-colors hover:bg-surface/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-grc focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
              >
                <div className={`text-[10px] font-bold uppercase tracking-r-eyebrow ${niveauColor[a.niveau] ?? ''}`}>
                  {a.niveau}
                </div>
                <div className="truncate text-xs font-medium text-ink">{a.titre}</div>
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <div className="rounded-r-md border border-success/30 bg-success/10 backdrop-blur-glass p-2.5">
          <div className="flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full bg-success" aria-hidden="true" />
            <span className="text-xs font-medium text-success">Aucune alerte en cours</span>
          </div>
        </div>
      )}

      <div className="mt-3 space-y-0.5 text-2xs text-ink-dim">
        <div><span className="text-success">●</span> info → site + mail</div>
        <div><span className="text-warning">●</span> avis → + push PWA</div>
        <div><span className="text-urgent">●</span> alerte → + SMS</div>
        <div><span className="text-ink">●</span> crise → + panneau + PCS</div>
      </div>

      <Button
        type="button"
        variant="grc"
        size="sm"
        className="mt-3 w-full"
        onClick={() => onNavigate('/grc/alertes')}
      >
        Gérer les alertes
      </Button>
    </Card>
  );
}

// =============================================================================
// NEWSLETTER PANEL — encart sidebar dashboard
// =============================================================================

function NewsletterPanel({
  onNavigate, abonnes,
}: {
  onNavigate: (path: string) => void;
  abonnes: number;
}) {
  const [data, setData] = useState<{
    items: Array<{
      id: string; titre: string; numero: number; statut: string;
      envoyeCount: number; ouvertCount: number; dateEnvoiReel: string | null;
    }>;
  } | null>(null);

  useEffect(() => {
    let cancelled = false;
    void api.grc.newsletters.list({ limit: 3 }).then((d) => {
      if (!cancelled) setData(d);
    }).catch(() => { /* silent */ });
    return () => { cancelled = true; };
  }, []);

  const lastSent = data?.items.find((n) => n.statut === 'envoyee');
  const draft = data?.items.find((n) => n.statut === 'brouillon');
  const tauxOuv = lastSent && lastSent.envoyeCount > 0
    ? Math.round((lastSent.ouvertCount / lastSent.envoyeCount) * 100)
    : null;

  return (
    <Card variant="glass" padded>
      <div className="mb-2 flex items-center justify-between">
        <SectionEyebrow as="h3">Newsletter municipale</SectionEyebrow>
        <span className="text-2xs text-ink-muted">
          {abonnes} abonné{abonnes > 1 ? 's' : ''}
        </span>
      </div>

      {lastSent ? (
        <button
          type="button"
          onClick={() => onNavigate(`/grc/newsletters/${lastSent.id}`)}
          className="block w-full rounded-r-md border border-grc/30 bg-grc-tint/40 backdrop-blur-glass p-2.5 text-left transition-all hover:bg-grc-tint/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-grc focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
        >
          <div className="text-[10px] uppercase tracking-r-eyebrow text-grc-fg">Dernière envoyée</div>
          <div className="truncate text-xs font-medium text-ink">N°{lastSent.numero} {lastSent.titre}</div>
          <div className="mt-1 flex items-center gap-2 text-2xs text-ink-muted">
            <MailOpen className="h-3 w-3" aria-hidden="true" /> {lastSent.envoyeCount} envois
            {tauxOuv !== null && <span className="text-grc-fg">· {tauxOuv}% ouverture</span>}
          </div>
        </button>
      ) : (
        <div className="rounded-r-md border border-border bg-surface/40 backdrop-blur-glass p-2.5 text-center">
          <p className="text-xs text-ink-muted">Aucune newsletter envoyée</p>
        </div>
      )}

      {draft && (
        <button
          type="button"
          onClick={() => onNavigate(`/grc/newsletters/${draft.id}/edit`)}
          className="mt-2 block w-full rounded-r-md border border-warning/30 bg-warning/10 backdrop-blur-glass p-2.5 text-left transition-all hover:bg-warning/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-warning focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
        >
          <div className="text-[10px] uppercase tracking-r-eyebrow text-warning">Brouillon en cours</div>
          <div className="truncate text-xs font-medium text-ink">N°{draft.numero} {draft.titre}</div>
        </button>
      )}

      <Button
        type="button"
        variant="grc"
        size="sm"
        className="mt-3 w-full"
        onClick={() => onNavigate('/grc/newsletters')}
      >
        Gérer les newsletters
      </Button>
    </Card>
  );
}
